import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ProgramAccount } from '@project-serum/anchor';
import { Connection, PublicKey } from '@solana/web3.js';

import {
  DraffleProgram,
  EntrantsData,
  RaffleDataRaw,
} from './ProgramApisProvider';
import { useProgramApis } from '../hooks/useProgramApis';
import { Entrant, Raffle, RaffleMetaData } from '../lib/types';
import {
  deserializeEntrantsData,
  fetchPrizes,
  fetchProceedsAccount,
  getRaffleProgramAccounts,
  toEntrantsProcessed,
} from '../lib/store';
import { cloneDeep } from 'lodash';
import { areEqualObjects } from '../lib/utils';
import { useConnection } from '@solana/wallet-adapter-react';
import { RAFFLES_WHITELIST } from '../config/raffleWhitelist';

export interface RafflesStore {
  raffles: Map<string, Raffle>;
  homeRaffles: Map<string, Raffle>;
  fetchAllRaffles: (includeEmpty?: boolean) => void;
  fetchHomeRaffles: (includeEmpty?: boolean) => void;
  updateRaffleById: (raffleId: string) => void;
  fetching: boolean;
  homeFetching: boolean;
}

const getAssociatedRaffleData = async (
  raffleRaw: ProgramAccount<RaffleDataRaw>,
  raffleMetaData: RaffleMetaData,
  draffleClient: DraffleProgram,
  connection: Connection,
  entrantsData?: EntrantsData
): Promise<Raffle> => {
  const proceedsAccount = await fetchProceedsAccount(
    raffleRaw.publicKey,
    draffleClient,
    connection
  );
  // console.log(proceedsAccount, 'PA');
  let entrants = new Map<string, Entrant>();
  if (!entrantsData) {
    try {
      const entrantsAccountInfo = await connection.getAccountInfo(
        raffleRaw.account.entrants
      );
      if (!entrantsAccountInfo)
        throw new Error('Cannot find entrants account info');
      entrantsData = deserializeEntrantsData(
        draffleClient,
        entrantsAccountInfo.data
      );
    } catch {
      // TODO: Merge ended raffle data stored off-chain here
      // console.log(`Raffle ${raffleRaw.publicKey} entrants account is closed`);

      entrantsData = {
        max: 0,
        total: 0,
        entrants: [],
      };
    }
  }

  entrants = toEntrantsProcessed(entrantsData);

  const prizes = await fetchPrizes(
    raffleRaw.publicKey,
    draffleClient,
    raffleRaw.account.totalPrizes
  );

  const endTimestamp = new Date(
    raffleRaw.account.endTimestamp.toNumber() * 1000
  );

  // console.log(raffleRaw.publicKey.toBase58(), 'rr');
  // console.log(proceedsAccount, 'rrinfo');

  // console.log(new Date(raffleRaw.account.endTimestamp.toNumber()));
  // console.log(new Date(raffleRaw.account.endTimestamp.toNumber() * 1000 ))

  return {
    featured: raffleRaw.account.featured,
    creator: raffleRaw.account.creator,
    publicKey: raffleRaw.publicKey,
    metadata: raffleMetaData,
    endTimestamp,
    entrantsCap: raffleRaw.account.maxTickets,
    entrants,
    entrantsRaw: entrantsData.entrants,
    totalTickets: !raffleRaw.account.claimedPrizes
      ? entrantsData.total
      : raffleRaw.account.ticketsSold,
    entrantsAccountAddress: raffleRaw.account.entrants,
    randomness: raffleRaw.account.randomness as number[],
    prizes,
    proceeds: {
      address: proceedsAccount.address,
      ticketPrice: raffleRaw.account.ticketPrice,
      mint: proceedsAccount.mintInfo,
    },
    isEnded: endTimestamp < new Date(),
    claimedPrizes: raffleRaw.account.claimedPrizes,
    proceedsCollected: !proceedsAccount.amount ? true : false,
  };
};

export const RafflesStoreContext = createContext<RafflesStore>({} as any);

const RafflesStoreProvider: FC = ({ children = null as any }) => {
  const { connection } = useConnection();
  const { draffleClient } = useProgramApis();

  const [homeFetching, setHomeFetching] = useState<boolean>(true); // prevents messy first render, but probably not optimal
  const [homeRaffles, setHomeRaffles] = useState<Map<string, Raffle>>(
    new Map<string, Raffle>()
  );

  const [fetching, setFetching] = useState<boolean>(true); // prevents messy first render, but probably not optimal
  const [raffles, setRaffles] = useState<Map<string, Raffle>>(
    new Map<string, Raffle>()
  );

  const fetchAllRaffles = useCallback(
    async (includeEmpty: boolean = false) => {
      setFetching(true);
      try {
        let { raffleDataRawProgramAccounts, entrantsDataProgramAccounts } =
          await getRaffleProgramAccounts(draffleClient);
        // raffleDataRawProgramAccounts = raffleDataRawProgramAccounts.filter(
        //   ({ publicKey }) =>
        //     includeEmpty
        // );
        const newRaffles = (
          await Promise.all(
            raffleDataRawProgramAccounts.map(async (raffleRaw) => {
              console.log(raffleRaw, 'raW')
              return getAssociatedRaffleData(
                raffleRaw,
                RAFFLES_WHITELIST.get(raffleRaw.publicKey.toString()) || {
                  name: 'Raffle',
                  alternatePurchaseMints: [],
                },
                draffleClient,
                connection,
                entrantsDataProgramAccounts.find(({ publicKey }) =>
                  publicKey.equals(raffleRaw.account.entrants)
                )?.account
              );
            })
          )
        ).reduce<Map<string, Raffle>>((acc, raffle) => {
          acc.set(raffle.publicKey.toString(), raffle);
          return acc;
        }, new Map<string, Raffle>());
        console.log(newRaffles, 'new raffles, fetchAllRaffles');
        setRaffles(newRaffles);
      } catch (e) {
        console.log(e);
      }

      setFetching(false);
    },
    [connection, draffleClient]
  );

  const fetchHomeRaffles = useCallback(
    async (includeEmpty: boolean = false) => {
      setHomeFetching(true);
      try {
        let { raffleDataRawProgramAccounts, entrantsDataProgramAccounts } =
          await getRaffleProgramAccounts(draffleClient);
        const newRaffles = (
          await Promise.all(
            raffleDataRawProgramAccounts.map(async (raffleRaw) => {
              // console.log(raffleRaw, 'raW')
              return getAssociatedRaffleData(
                raffleRaw,
                RAFFLES_WHITELIST.get(raffleRaw.publicKey.toString()) || {
                  name: 'Raffle',
                  alternatePurchaseMints: [],
                },
                draffleClient,
                connection,
                entrantsDataProgramAccounts.find(({ publicKey }) =>
                  publicKey.equals(raffleRaw.account.entrants)
                )?.account
              );
            })
          )
        ).reduce<Map<string, Raffle>>((acc, raffle) => {
          acc.set(raffle.publicKey.toString(), raffle);
          return acc;
        }, new Map<string, Raffle>());
        // console.log(newRaffles, 'new raffles');

        newRaffles.forEach((item: Raffle, key: any) => {
          if (item.claimedPrizes) {
            newRaffles.delete(key);
          } else if (item.isEnded && item.entrants.size === 0) {
            newRaffles.delete(key);
          }
        });
        setHomeRaffles(newRaffles);
      } catch (e) {
        console.log(e);
      }

      setHomeFetching(false);
    },
    [connection, draffleClient]
  );

  const updateRaffleById = useCallback(
    async (raffleId: string) => {
      // console.log('updating raffle : ', raffleId)
      if (!homeRaffles.has(raffleId.toString()))
        return;
      setFetching(true);
      // console.log('set fetching true')
      //@ts-ignore
      const updatedRaffleRaw = await draffleClient.account.raffle.fetch(
        new PublicKey(raffleId)
      );
      // console.log("got raw data ",updatedRaffleRaw)
      const updatedRaffle = await getAssociatedRaffleData(
        { publicKey: new PublicKey(raffleId), account: updatedRaffleRaw },
        {
          name: 'Raffle',
          alternatePurchaseMints: [],
        },
        draffleClient,
        connection
      );
      // console.log('got metadata ', updatedRaffle)
      if (!areEqualObjects(homeRaffles.get(raffleId.toString()), updatedRaffle)) {
        // console.log('setting updated raffle')
        setHomeRaffles((currentRaffles) => {
          let newRaffles = cloneDeep(currentRaffles);
          newRaffles = newRaffles.set(raffleId, updatedRaffle);
          // console.log(newRaffles, 'nr')
          return newRaffles;
        });
      }
      setFetching(false);
    },
    [connection, draffleClient, homeRaffles, setHomeRaffles]
  );

  useEffect(() => {
    // console.log('fetch home raffles called');
    fetchHomeRaffles();
  }, [fetchHomeRaffles]);

  useEffect(() => {
    // console.log('fetch all raffles called');
    fetchAllRaffles();
  }, [fetchAllRaffles]);

  return (
    <RafflesStoreContext.Provider
      value={{
        raffles,
        homeRaffles,
        fetchAllRaffles,
        fetchHomeRaffles,
        updateRaffleById,
        fetching,
        homeFetching,
      }}
    >
      {children}
    </RafflesStoreContext.Provider>
  );
};

export default RafflesStoreProvider;
